(() => {
  if (customElements.get('aof-journal-index')) return;
  const normalize = (value) => String(value ?? '').normalize('NFKC').toLocaleLowerCase().replace(/\s+/gu, ' ').trim();
  class AofJournalIndex extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.query = this.querySelector('[name="q"]');
      this.results = this.querySelector('[data-journal-results]');
      this.status = this.querySelector('[data-journal-status]');
      this.more = this.querySelector('[data-journal-more]');
      this.retry = this.querySelector('[data-journal-retry]');
      this.sources = [...this.querySelectorAll('[data-journal-source]')].map((link) => ({
        url: link.href, handle: link.dataset.journalSource, expected: Number(link.dataset.journalTotal), articles: [], complete: false
      }));
      this.limit = 24;
      this.busy = false;
      this.querySelector('form').addEventListener('submit', (event) => { event.preventDefault(); this.search(); });
      this.query.addEventListener('input', () => { clearTimeout(this.timer); this.timer = setTimeout(() => this.search(), 180); });
      this.more.addEventListener('click', () => { this.limit += 24; this.draw(); });
      this.retry.addEventListener('click', () => this.load());
      this.load();
    }
    search() {
      this.limit = 24;
      const url = new URL(location.href);
      if (this.query.value.trim()) url.searchParams.set('q', this.query.value.trim());
      else url.searchParams.delete('q');
      history.replaceState(null, '', url);
      this.draw();
    }
    async loadSource(source) {
      const articles = new Map();
      let pages = 1;
      for (let page = 1; page <= pages; page += 1) {
        const url = new URL(source.url, location.origin);
        url.search = '';
        url.searchParams.set('view', 'aof-journal-feed');
        url.searchParams.set('page', String(page));
        const theme = window.Shopify?.theme;
        if (theme?.id && theme.role !== 'main') url.searchParams.set('preview_theme_id', String(theme.id));
        const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(20000) });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (data.blog !== source.handle || !Array.isArray(data.articles) || Number(data.page) !== page || !Number.isInteger(Number(data.pages))) throw new Error('Invalid journal feed');
        pages = Math.max(1, Number(data.pages));
        if (pages > 500) throw new Error('Journal pagination exceeds limit');
        source.expected = Number(data.total);
        for (const article of data.articles) {
          const link = new URL(article.url, location.origin);
          if (link.origin !== location.origin || !link.pathname.startsWith('/blogs/') || !Number.isFinite(Date.parse(article.publishedAt))) throw new Error('Invalid article');
          article.url = link.pathname;
          article.index = normalize([article.title, article.blog, ...(article.tags || []), article.searchText].join(' '));
          articles.set(String(article.id), article);
        }
      }
      if (articles.size !== source.expected) throw new Error('Incomplete journal feed');
      source.articles = [...articles.values()];
      source.complete = true;
    }
    async load() {
      if (this.busy) return;
      this.busy = true;
      this.retry.hidden = true;
      this.results.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loading;
      const pending = this.sources.filter((source) => !source.complete);
      const worker = async () => {
        while (pending.length) {
          const source = pending.shift();
          try { await this.loadSource(source); } catch { source.complete = false; }
        }
      };
      await Promise.all([worker(), worker()]);
      this.busy = false;
      this.results.setAttribute('aria-busy', 'false');
      this.draw();
    }
    draw() {
      const all = [...new Map(this.sources.flatMap((source) => source.articles).map((article) => [String(article.id), article])).values()];
      all.sort((a, b) => Date.parse(b.publishedAt) - Date.parse(a.publishedAt) || String(b.id).localeCompare(String(a.id)));
      const terms = normalize(this.query.value).split(' ').filter(Boolean);
      const matches = all.filter((article) => terms.every((term) => article.index.includes(term)));
      const complete = this.sources.length === 6 && this.sources.every((source) => source.complete);
      const count = this.dataset.count.replace('{matched}', String(matches.length)).replace('{loaded}', String(all.length));
      this.status.textContent = `${complete ? this.dataset.complete : (this.busy ? this.dataset.loading : this.dataset.partial)} ${count}`;
      this.retry.hidden = complete || this.busy;
      const fragment = document.createDocumentFragment();
      for (const article of matches.slice(0, this.limit)) {
        const card = document.createElement('article');
        card.className = 'aof-journal-result';
        const meta = document.createElement('div');
        meta.className = 'aof-journal-result__meta';
        const date = document.createElement('time');
        date.dateTime = article.publishedAt;
        date.textContent = article.date;
        meta.append(date, document.createTextNode(` / ${article.blog}`));
        const heading = document.createElement('h2');
        const link = document.createElement('a');
        link.href = article.url;
        link.textContent = article.title;
        heading.append(link);
        const excerpt = document.createElement('p');
        excerpt.textContent = article.excerpt;
        const media = document.createElement('div');
        media.className = 'aof-journal-media aof-journal-result__media';
        const placeholder = () => {
          const template = this.querySelector('[data-journal-placeholder]');
          media.replaceChildren(template.content.cloneNode(true));
        };
        placeholder();
        if (article.image?.src) {
          try {
            const imageUrl = (value) => {
              const url = new URL(value, location.origin);
              if (!['https:', 'http:'].includes(url.protocol) || (url.origin !== location.origin && url.hostname !== 'cdn.shopify.com')) throw new Error('Invalid image URL');
              return url.href;
            };
            const image = document.createElement('img');
            image.className = 'aof-journal-image';
            image.alt = String(article.image.alt || '');
            image.width = Number(article.image.width) || 320;
            image.height = Number(article.image.height) || 213;
            image.loading = fragment.childElementCount < 2 ? 'eager' : 'lazy';
            image.decoding = 'async';
            image.addEventListener('error', placeholder, { once: true });
            image.src = imageUrl(article.image.src);
            if (article.image.src2x) image.srcset = `${imageUrl(article.image.src)} 320w, ${imageUrl(article.image.src2x)} 640w`;
            image.sizes = '(max-width: 700px) 96px, 144px';
            media.replaceChildren(image);
          } catch { placeholder(); }
        }
        const copy = document.createElement('div');
        copy.className = 'aof-journal-result__copy';
        copy.append(meta, heading, excerpt);
        card.append(media, copy);
        fragment.append(card);
      }
      if (!matches.length && complete) {
        const empty = document.createElement('p');
        empty.textContent = this.dataset.empty;
        fragment.append(empty);
      }
      this.results.replaceChildren(fragment);
      this.more.hidden = matches.length <= this.limit;
      this.querySelector('[data-journal-fallback]').hidden = all.length > 0;
    }
  }
  customElements.define('aof-journal-index', AofJournalIndex);
})();
